import { inject, Injectable } from '@angular/core';
import { createContextLogger } from '@konnektu/helpers';
import { equal, from, MetastoreService } from '@konnektu/metastore';
import { map, of, shareReplay, tap } from 'rxjs';

export interface DynamicViewListItem {
  Id: string;
  Path: string;
  Type: string;
}

export interface DynamicViewConfig extends DynamicViewListItem {
  Payload: string;
}

@Injectable({ providedIn: 'root' })
export class CardViewService {
  private readonly logger = createContextLogger('CardViewService');

  private readonly metastore = inject(MetastoreService);

  private readonly views$ = this.metastore
    .select<DynamicViewListItem>(
      from('UIDynamicView')
        .select(['Id', 'Path', 'Type'])
        .orderBy('CreatedOn', 'asc')
    )
    .pipe(
      tap((views) => this.logger.debug('All views loaded', views)),
      shareReplay(1)
    );

  private readonly payloadCache = new Map<string, DynamicViewConfig>();

  getAllViews() {
    return this.views$;
  }

  getViews(type: 'card' | 'section') {
    return this.getAllViews().pipe(
      map((views) => views.filter((view) => view.Type === type))
    );
  }

  getView(path: string, type: 'card' | 'section') {
    const cacheKey = `${type}:${path}`;
    const cached = this.payloadCache.get(cacheKey);
    if (cached) {
      return of(cached);
    }
    return this.metastore
      .select<DynamicViewConfig>(
        from('UIDynamicView')
          .select(['Id', 'Path', 'Type', 'Payload'])
          .where(equal('Path', path))
          .orderBy('CreatedOn', 'asc')
      )
      .pipe(
        map(
          (views) =>
            views.find((view) => view.Type === type && view.Path === path) ??
            null
        ),
        tap((view) => {
          if (view) {
            this.payloadCache.set(cacheKey, view);
          }
        })
      );
  }
}
